import React, { useState, useEffect } from 'react';
// Button removed - causes production crash
import { Card } from './ui/card';
import { Download, X, Smartphone, Monitor } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed via localStorage
    const wasInstalled = localStorage.getItem('pwa-installed') === 'true';
    const wasDismissed = localStorage.getItem('pwa-prompt-dismissed') === 'true';
    
    if (wasInstalled || wasDismissed) {
      setIsInstalled(wasInstalled);
      return; // Don't show prompt if already installed or dismissed permanently
    }

    // Check if already installed via display-mode
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      localStorage.setItem('pwa-installed', 'true');
      return;
    }

    // Listen for install prompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check again if user has dismissed or installed before
      const hasShownBefore = localStorage.getItem('pwa-prompt-shown');
      const hasInstalled = localStorage.getItem('pwa-installed') === 'true';
      const hasDismissed = localStorage.getItem('pwa-prompt-dismissed') === 'true';
      
      if (!hasShownBefore && !hasInstalled && !hasDismissed) {
        setTimeout(() => setShowPrompt(true), 3000); // Show after 3 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app was installed
    window.addEventListener('appinstalled', () => {
      console.log('✅ PWA was installed');
      setIsInstalled(true);
      setShowPrompt(false);
      localStorage.setItem('pwa-installed', 'true');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show install prompt
    deferredPrompt.prompt();

    // Wait for user response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to install prompt: ${outcome}`);

    if (outcome === 'accepted') {
      console.log('✅ User accepted install');
      localStorage.setItem('pwa-installed', 'true');
    } else {
      console.log('❌ User dismissed install');
      localStorage.setItem('pwa-prompt-shown', 'true');
    }

    // Clear the prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-shown', 'true');
  };

  // Permanently dismiss - won't show again until localStorage is cleared
  const handleDismissPermanently = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Get device type
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (isInstalled || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-slide-up">
      <Card className="p-4 bg-gradient-to-br from-purple-900/95 to-red-900/95 border-purple-600 shadow-2xl backdrop-blur-sm">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-600 rounded-lg">
              {isMobile ? (
                <Smartphone className="w-6 h-6 text-white" />
              ) : (
                <Monitor className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Install POS Terminal</h3>
              <p className="text-sm text-purple-200">
                {isMobile ? 'Quick access on your device' : 'Install for cashier workstation'}
              </p>
            </div>
          </div>
          <button
            onClick={handleDismissPermanently}
            className="text-purple-300 hover:text-white transition-colors"
            title="Don't show again"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex items-start gap-2 text-sm text-purple-100">
            <span className="text-purple-400">✓</span>
            <span>Direct access to POS Terminal</span>
          </div>
          <div className="flex items-start gap-2 text-sm text-purple-100">
            <span className="text-purple-400">✓</span>
            <span>Works offline during network issues</span>
          </div>
          <div className="flex items-start gap-2 text-sm text-purple-100">
            <span className="text-purple-400">✓</span>
            <span>Fast order taking - no browser needed</span>
          </div>
          <div className="flex items-start gap-2 text-sm text-purple-100">
            <span className="text-purple-400">✓</span>
            <span>Auto-sync orders when reconnected</span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleInstallClick}
            className="flex-1 bg-white text-purple-900 hover:bg-purple-50 font-semibold px-4 py-2 rounded-md flex items-center justify-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Install Now
          </button>
          <button
            onClick={handleDismiss}
            className="border border-purple-400 text-purple-100 hover:bg-purple-800/50 px-4 py-2 rounded-md"
          >
            Later
          </button>
        </div>

        <p className="text-xs text-purple-300 mt-3 text-center">
          Free • No download from store needed
        </p>
      </Card>
    </div>
  );
};

export default PWAInstallPrompt;
